import React, { useContext, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ImageInputProps } from './types';
import {
  ButtonContainer,
  HiddenInput,
  ImageContainer,
  ImageInputContainer,
  ImageNameContainer,
  ImagePreview,
  NoImageContainer,
  NoImageTextContainer,
} from './styles';
import Button from '../Button/Button';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import Tooltip, { ToolTipPosition } from '../Tooltip/Tooltip';
import { ErrorMessage, HelperMessage, HelperContainer } from '../Input/styles';

export const ImageInput = ({
  input,
  meta,
  helperText,
  canDelete = true,
  orientation = 'square',
  replaceText = 'Reemplazar',
  uploadText = 'Seleccionar archivo',
  formatText = 'Formato png o jpeg',
  testId,
  disabled,
  onChange,
}: ImageInputProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { actualTheme } = useContext(ThemeContext);
  const hasError = meta && meta.invalid && !meta.pristine;
  const [imgError, setImgError] = useState(false);

  const processFile = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      setFileName(file.name);
      reader.onload = () => {
        const result = reader.result;
        input.onChange(result);
        onChange?.(result);
        const img = new Image();
        img.src = result.toString();

        img.onload = () => {
          setImgError(false);
        };

        img.onerror = () => {
          setImgError(true);
        };
      };
    }
  };

  const handleUpload = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleClear = () => {
    input.onChange('');
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    processFile(file);
  };

  const showHelperOrError = () => {
    if (hasError) {
      return (
        <>
          <ErrorMessage data-testid={`${testId}-error`}>{meta.error}</ErrorMessage>
        </>
      );
    }

    if (helperText)
      return (
        <HelperMessage disabled={disabled} data-testid={`${testId}-help-message`}>
          {helperText}
        </HelperMessage>
      );

    return null;
  };

  const getIconColor = () => {
    if (disabled) return '#ccc';
    if (hasError) return '#ff1515';
    return '#ff6c0e';
  };


  return (
    <ThemeProvider theme={actualTheme}>
      <ImageInputContainer hasError={hasError}>
        <ImageContainer
          data-testid={`${testId}-image-container`}
          imageUploaded={!!input.value}
          orientation={orientation}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragging={isDragging}
          onClick={handleUpload}
          disabled={disabled}
        >
          {input.value && !imgError ? (
            <ImagePreview src={input.value} alt=""  />
          ) : (
            <NoImageContainer>
              <BasicIcon
                clickable={!disabled}
                name={imgError ? "image" : "upload"}
                size="large"
                color={getIconColor()}
              />
              {!imgError && (
                <NoImageTextContainer disabled={disabled}>
                  <strong data-testid={`${testId}-upload-text`}>{uploadText}</strong>
                  <p data-testid={`${testId}-format-text`}>{formatText}</p>
                </NoImageTextContainer>
              )}
            </NoImageContainer>
          )}
        </ImageContainer>
        {input.value && (
          <ButtonContainer>
            <ImageNameContainer>
              <p data-testid={`${testId}-file-name`}>{fileName}</p>
              {canDelete && (
                <Button
                  onClick={handleClear}
                  iconName="trash"
                  hierarchy="secondary"
                  testId={`${testId}-delete-button`}
                />
              )}
            </ImageNameContainer>
            <Tooltip message={replaceText} position={ToolTipPosition.top}>
              <Button
                onClick={handleUpload}
                iconName="switch-vertical"
                hierarchy="secondary"
                testId={`${testId}-replace-button`}
              />
            </Tooltip>
          </ButtonContainer>
        )}
      </ImageInputContainer>
      <HelperContainer>{showHelperOrError()}</HelperContainer>

      <HiddenInput {...input} />
      <HiddenInput type="file" ref={inputRef} onChange={handleImageChange} accept="image/*" />
    </ThemeProvider>
  );
};
